import React, { useEffect, useState } from 'react';

import { Button, Divider, Group, Paper, Stack, Text, Title, Tooltip } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import GoogleCalendarSelect from 'components/form-fields/auto-complete-fields/GoogleCalendarSelect';
import KiloAlert from 'components/KiloAlert';
import useUpdateSyncedCalendar from 'hooks/googleCalendarIntegration/useUpdateSyncedCalendar';
import { GoogleCalendarIntegration } from 'types';
import notificationHandler from 'utils/notifications';

import EventTypeCheckbox from './EventTypeCheckbox';
import ForceResyncModal from './ForceResyncModal';
import SyncedCalendarChangeModal from './SyncedCalendarChangeModal';

export interface FormValues {
  googleCalendarIntegrationId: string;
  syncedCalendar: string | null;
}

interface SynchronizationCardProps {
  googleCalendarIntegration: GoogleCalendarIntegration;
}

const SynchronizationCard = ({ googleCalendarIntegration }: SynchronizationCardProps): JSX.Element => {
  const [syncSettings, setSyncSettings] = useState({
    syncAppointmentsToCalendar: googleCalendarIntegration.syncAppointmentsToCalendar,
    syncClassesToCalendar: googleCalendarIntegration.syncClassesToCalendar,
  });
  const [syncSettingsModified, setSyncSettingsModified] = useState(false);

  const [isSyncedCalendarModalOpen, { close: closeSyncedCalendarModal, open: openSyncedCalendarModal }] =
    useDisclosure(false);
  const [isForceResyncModalOpen, { close: closeForceResyncModal, open: openForceResyncModal }] =
    useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();
  const updateSyncedCalendarMutation = useUpdateSyncedCalendar();

  const form = useForm<FormValues>({
    initialValues: {
      googleCalendarIntegrationId: googleCalendarIntegration.googleCalendarIntegrationId,
      syncedCalendar: googleCalendarIntegration.syncedCalendar
        ? googleCalendarIntegration.syncedCalendar.googleCalendarId
        : null,
    },
  });

  const handleResetSyncSettings = (): void => {
    setSyncSettings({
      syncAppointmentsToCalendar: googleCalendarIntegration.syncAppointmentsToCalendar,
      syncClassesToCalendar: googleCalendarIntegration.syncClassesToCalendar,
    });
    setSyncSettingsModified(false);
  };

  const handleUpdateSyncedCalendar = (values: FormValues): void => {
    if (values.syncedCalendar !== null) {
      const payload = {
        ...values,
        syncedCalendar: values.syncedCalendar,
      };
      updateSyncedCalendarMutation.mutate(payload, {
        onError: error => {
          errorNotification('Integration update failed', error.response?.data.message);
        },
        onSuccess: () => {
          successNotification(
            'Integration updated. Calendar synchronization is happening in the background',
          );
          closeSyncedCalendarModal();
          handleResetSyncSettings();
          form.resetDirty();
        },
      });
    }
  };

  useEffect(() => {
    if (
      googleCalendarIntegration.syncAppointmentsToCalendar !== syncSettings.syncAppointmentsToCalendar ||
      googleCalendarIntegration.syncClassesToCalendar !== syncSettings.syncClassesToCalendar
    ) {
      setSyncSettingsModified(true);
    } else {
      setSyncSettingsModified(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleCalendarIntegration]);

  return (
    <Paper radius='lg' p='xl' shadow='xs' withBorder>
      <Stack>
        <Stack spacing='xs'>
          <Group position='apart'>
            <Title order={3}>Synchronization</Title>
            <Tooltip
              label='A sync was requested recently. Please allow the system time to update.'
              hidden={googleCalendarIntegration.canSync}
            >
              <div>
                <Button
                  onClick={openForceResyncModal}
                  variant={syncSettingsModified ? undefined : 'outline'}
                  disabled={!googleCalendarIntegration.canSync || form.isDirty()}
                >
                  Force Resync
                </Button>
              </div>
            </Tooltip>
          </Group>
          <Text color='dimmed'>{'Configure how Kilo adds events to your Google calendar.'}</Text>
        </Stack>

        <Divider mx='-xl' />

        <Stack>
          <Text color='dimmed'>{'What should we sync?'}</Text>
          <EventTypeCheckbox
            googleCalendarIntegration={googleCalendarIntegration}
            type='syncAppointmentsToCalendar'
          />
          <EventTypeCheckbox
            googleCalendarIntegration={googleCalendarIntegration}
            type='syncClassesToCalendar'
          />
          {syncSettingsModified && googleCalendarIntegration.syncedCalendar && !form.isDirty() && (
            <KiloAlert>
              It looks like you have modified your sync configuration. Use <strong>Force Resync</strong> to
              apply these changes to your Google Calendar.
            </KiloAlert>
          )}
        </Stack>

        <Divider mx='-xl' />

        <form id='synced-calendar-form' onSubmit={form.onSubmit(handleUpdateSyncedCalendar)}>
          <Stack>
            <Text color='dimmed'>{'Where should we sync it?'}</Text>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'auto min-content',
                alignItems: 'center',
                gap: '1rem',
              }}
            >
              <GoogleCalendarSelect
                placeholder='Select a calendar'
                googleCalendarIntegrationId={form.values.googleCalendarIntegrationId}
                {...form.getInputProps('syncedCalendar')}
              />
              {form.values.syncedCalendar &&
                googleCalendarIntegration.syncedCalendar?.googleCalendarId !==
                  form.values.syncedCalendar && (
                  <Tooltip
                    label='A sync was requested recently. Please allow the system time to update.'
                    hidden={googleCalendarIntegration.canSync}
                  >
                    <div>
                      <Button
                        onClick={openSyncedCalendarModal}
                        loading={updateSyncedCalendarMutation.isLoading}
                        disabled={
                          !googleCalendarIntegration.canSync ||
                          (!googleCalendarIntegration.syncAppointmentsToCalendar &&
                            !googleCalendarIntegration.syncClassesToCalendar)
                        }
                      >
                        Save
                      </Button>
                    </div>
                  </Tooltip>
                )}
            </div>
          </Stack>
        </form>
        {!googleCalendarIntegration.syncedCalendar && !form.values.syncedCalendar && (
          <KiloAlert>Select a calendar to enable synchronization.</KiloAlert>
        )}
      </Stack>

      <SyncedCalendarChangeModal
        opened={isSyncedCalendarModalOpen}
        onClose={closeSyncedCalendarModal}
        googleCalendarIntegration={googleCalendarIntegration}
        syncedCalendarId={form.values.syncedCalendar}
        updateSyncedCalendarMutation={updateSyncedCalendarMutation}
      />
      <ForceResyncModal
        opened={isForceResyncModalOpen}
        onClose={closeForceResyncModal}
        googleCalendarIntegration={googleCalendarIntegration}
        handleResetSyncSettings={handleResetSyncSettings}
      />
    </Paper>
  );
};

export default SynchronizationCard;
